/**
 * RLS-guarded client WRITES: registered RPC wrappers, called in the database's own region.
 *
 * ## Why this is not the side door CLAUDE.md §4 forbids
 *
 * "The dashboard consumes the public API. No privileged side doors." A `.from().insert()` from the
 * browser would be one — a second way into the tables with none of the checks the route made. This
 * is the opposite shape: every function here is a NAMED `public` wrapper (ADR 0049 step 4) that
 * re-applies, in SQL, the four things the Worker route added around the `app_private` transaction:
 *
 *   1. the permission key (`app_private.current_user_holds`), which the inner function does not check;
 *   2. idempotency (api-platform.md §3), through the same `claim/complete_idempotent_request` the
 *      Worker used, so a retried request replays instead of creating a second envelope;
 *   3. the client's IP and user agent, DERIVED server-side from `cf-connecting-ip` — never accepted
 *      as arguments, because they land on an append-only, court-facing chain;
 *   4. the request validation the route did before touching the database.
 *
 * The wrappers are SECURITY DEFINER precisely so the `app_private` functions stay unreachable: a
 * signed-in caller holds EXECUTE on the wrapper and on nothing beneath it. Verified on the cell —
 * `authenticated` cannot call `app_private.create_envelope`, and the migration refuses to apply if
 * that ever becomes true.
 *
 * `infra/client-reads.json` registers every one under `rpc`, and `pnpm lint:client-reads` fails the
 * build on an unregistered `.rpc()` — the section was empty until this file, so the gate was already
 * refusing every RPC before there was one to allow.
 *
 * ## Errors
 *
 * The wrappers raise with SQLSTATE `PTxxx`, which PostgREST turns into HTTP xxx: 403 for a
 * permission, 409 for a claim already in flight, 422 for a validation failure or a key reused with a
 * different body. `WriteError` carries the code so a screen can tell "you may not" from "try again".
 */
import { api } from './client'
import { getClient } from '../supabase'
import type { EnvelopeResource } from './types'

export class WriteError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'WriteError'
  }
}

function client() {
  const c = getClient()
  if (c === null) {
    throw new WriteError('not_configured', 'this deployment has no Supabase configured')
  }
  return c
}

/** True when this build can reach the database's Data API. */
export function writesConfigured(): boolean {
  return getClient() !== null
}

/**
 * `POST /v1/envelopes` — create a draft envelope.
 *
 * The idempotency key is minted by the CALLER and passed explicitly rather than generated in here,
 * so a component that retries a failed submit can retry with the SAME key. A key minted inside this
 * function would be new on every call, which is the shape that turns a double-click into two
 * envelopes in front of the same counterparty — the exact thing idempotency exists to prevent.
 */
export async function createEnvelope(input: {
  readonly idempotencyKey: string
  /**
   * Omitted by the international composer, which creates the draft the moment the first file is
   * dropped — before there is a filename to name it after (founder decision D8). The wrapper then
   * stores a placeholder marked `subject_source = 'auto'`, and the subject follows the first
   * document's title until somebody types one.
   *
   * Under `flowza_sign` the wrapper REFUSES a missing subject with PT422, so omitting it there is
   * an error rather than a shortcut — which is the point: the existing flow is unchanged.
   */
  readonly subject?: string
  readonly message?: string | null
  readonly timezone?: string | null
  readonly expiresAt?: string | null
  /**
   * Whether the people on this envelope may see each other's names and progress.
   *
   * Off unless asked for, matching the wrapper's own default and the fail-closed read behind it.
   * The same envelope shape carries a two-party negotiation, so disclosure is the sender's call
   * per envelope rather than a product-wide one — see 20260901018200.
   */
  readonly discloseProgress?: boolean
}): Promise<EnvelopeResource> {
  const { data, error } = await client().rpc('create_envelope', {
    p_idempotency_key: input.idempotencyKey,
    p_subject: input.subject ?? null,
    p_message: input.message ?? null,
    p_timezone: input.timezone ?? null,
    p_expires_at: input.expiresAt ?? null,
    p_disclose_progress: input.discloseProgress === true,
  })
  if (error) throw new WriteError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new WriteError('unexpected_shape', 'the envelope could not be read back')
  }
  const row = data as Record<string, unknown>
  if (typeof row['id'] !== 'string') {
    throw new WriteError('unexpected_shape', 'the envelope came back without an id')
  }
  return row as unknown as EnvelopeResource
}

/**
 * `POST /v1/envelopes/{id}/signers` — add a recipient to a DRAFT envelope.
 *
 * The envelope id goes over the wire as a bare uuid: the API's `env_` prefix is a resource-id
 * convention, and the wrapper's parameter is typed `uuid`, so PostgREST would refuse the prefixed
 * form. Stripping it here keeps the prefix an API-surface detail rather than pushing it into SQL.
 *
 * Adding to an envelope that has already been sent answers 404, not 409, and that is deliberate on
 * the server: one answer for "no such envelope", "not yours" and "already sent", because whether a
 * counterparty has been mailed yet is not a fact an unauthorized caller should be able to probe for.
 */
export async function addRecipient(input: {
  readonly idempotencyKey: string
  readonly envelopeId: string
  /**
   * Omitted for a SELF-seat, where the wrapper resolves the caller's own name and address from
   * their profile. Passing either alongside `self` is REFUSED (PT422) rather than ignored: a
   * caller who could pass them could seat anybody under the name of anybody.
   */
  readonly name?: string
  readonly email?: string
  readonly kind?: string
  readonly routingOrder?: number
  readonly locale?: string | null
  readonly phone?: string | null
  /** "I also need to sign the document" (D8). The caller is seated from their own profile. */
  readonly self?: boolean
}): Promise<Record<string, unknown>> {
  const { data, error } = await client().rpc('add_envelope_recipient', {
    p_idempotency_key: input.idempotencyKey,
    p_envelope_id: input.envelopeId.replace(/^env_/, ''),
    p_name: input.name ?? null,
    p_email: input.email ?? null,
    p_phone: input.phone ?? null,
    p_kind: input.kind ?? 'signer',
    p_routing_order: input.routingOrder ?? 1,
    p_locale: input.locale ?? null,
    p_self: input.self === true,
  })
  if (error) throw new WriteError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new WriteError('unexpected_shape', 'the recipient could not be read back')
  }
  const row = data as Record<string, unknown>
  if (typeof row['id'] !== 'string') {
    throw new WriteError('unexpected_shape', 'the recipient came back without an id')
  }
  return row
}

/**
 * `POST /v1/envelopes/{id}/void` — withdraw an envelope, with a reason.
 *
 * The first wrapper whose refusals are as load-bearing as its success. Two of them reach this
 * screen and mean different things: `PT409` is "this envelope has already closed" — nothing about
 * the request is wrong, and the screen should stop offering the button — while `PT404` is the one
 * answer the server gives for "no such envelope", "not yours" and, at other endpoints, "already
 * sent". Both arrive as a `WriteError` carrying the code, which is why `messageOf` prints it.
 *
 * `notified` is a count of the invited people who were told. It is never a list of addresses, on
 * the server or here.
 */
export async function voidEnvelope(input: {
  readonly idempotencyKey: string
  readonly envelopeId: string
  readonly reason: string
}): Promise<{ readonly notified: number; readonly voidedFrom: string }> {
  const { data, error } = await client().rpc('void_envelope', {
    p_idempotency_key: input.idempotencyKey,
    p_envelope_id: input.envelopeId.replace(/^env_/, ''),
    p_reason: input.reason,
  })
  if (error) throw new WriteError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new WriteError('unexpected_shape', 'the withdrawal could not be read back')
  }
  const row = data as Record<string, unknown>
  if (typeof row['notified'] !== 'number') {
    throw new WriteError('unexpected_shape', 'the withdrawal came back without a count')
  }
  return {
    notified: row['notified'],
    voidedFrom: typeof row['voided_from'] === 'string' ? row['voided_from'] : 'unknown',
  }
}

/**
 * `POST /v1/envelopes/{id}/signers/{signer_id}/reassign` — hand one recipient's part to somebody
 * else.
 *
 * The MEMBER path only. A signer handing off their OWN part goes through the ceremony's
 * `/sign/reassign`, which arrives with a signer session rather than an access token; the wrapper
 * would not recognise that caller and is not meant to.
 *
 * This endpoint has more distinct refusals than the rest of the composer put together, and the
 * screen shows every one verbatim because each names a different thing to do about it: `PT409`
 * with "this recipient has already finished with the document" is not the same problem as `PT409`
 * with "this envelope is not open for signing". The server keeps the sentences the API published;
 * this passes them through untouched.
 */
export async function reassignSigner(input: {
  readonly idempotencyKey: string
  readonly envelopeId: string
  readonly signerId: string
  readonly name: string
  readonly email: string
  readonly reason?: string | null
}): Promise<{ readonly delegateId: string; readonly delegateName: string }> {
  const { data, error } = await client().rpc('reassign_signer', {
    p_idempotency_key: input.idempotencyKey,
    p_envelope_id: input.envelopeId.replace(/^env_/, ''),
    // Either spelling of the prefix. The list this id came from emits `sgn_` (api-platform.md
    // §1.2); the recipients step emitted `sig_` for a day (ADR 0056). One regex, not two calls:
    // a bare uuid passes through untouched either way.
    p_envelope_signer_id: input.signerId.replace(/^s(?:gn|ig)_/, ''),
    p_name: input.name,
    p_email: input.email,
    p_reason: input.reason ?? null,
  })
  if (error) throw new WriteError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new WriteError('unexpected_shape', 'the handoff could not be read back')
  }
  const row = data as Record<string, unknown>
  const delegate = row['delegate']
  if (delegate === null || typeof delegate !== 'object') {
    throw new WriteError('unexpected_shape', 'the handoff came back without a delegate')
  }
  const d = delegate as Record<string, unknown>
  if (typeof d['id'] !== 'string' || typeof d['name'] !== 'string') {
    throw new WriteError('unexpected_shape', 'the delegate came back without an id or a name')
  }
  return { delegateId: d['id'], delegateName: d['name'] }
}

/**
 * `public.start_correction` — the sender opens a correction on an in-flight envelope.
 *
 * The database freezes the current revision (the hashes the carry-forward rule will compare
 * against), appends `envelope.correction_started` and cancels the reminders. Recipients' links
 * stay open, onto an on-hold screen, until the corrected revision is issued.
 *
 * `notify` (20260915005230) decides whether the people on the envelope are also MAILED that
 * signing is paused. False is the pause that shipped and the default here as well as in the
 * database: a pause that lasts as long as swapping one file would otherwise put two messages in
 * everybody's inbox for one event. The notice reaches only the recipients who already hold an
 * invitation — a later routing hop has not been told this envelope exists, and a pause is not the
 * moment to tell them — so `notified` can legitimately be smaller than the recipient list, and is
 * a COUNT rather than a list because the screen asking already has the names.
 */
export async function startCorrection(input: {
  readonly idempotencyKey: string
  readonly envelopeId: string
  readonly reason?: string | null
  readonly notify?: boolean
}): Promise<{ readonly revisionNumber: number; readonly notified: number }> {
  const { data, error } = await client().rpc('start_correction', {
    p_idempotency_key: input.idempotencyKey,
    p_envelope_id: input.envelopeId.replace(/^env_/, ''),
    p_reason: input.reason ?? null,
    p_notify: input.notify ?? false,
  })
  if (error) throw new WriteError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new WriteError('unexpected_shape', 'the correction could not be read back')
  }
  const row = data as Record<string, unknown>
  if (typeof row['revision_number'] !== 'number') {
    throw new WriteError('unexpected_shape', 'the correction came back without a revision')
  }
  // A Worker or a cell that predates the notify column answers without the count. That is a
  // deployment older than this client, not a broken write — the pause itself landed — so the
  // screen reads zero and says nothing about who was told, rather than refusing a correction the
  // database has already opened.
  const notified = row['notified']
  return {
    revisionNumber: row['revision_number'],
    notified: typeof notified === 'number' ? notified : 0,
  }
}

/**
 * `public.apply_quiet_edit` — the sender closes their own pause WITHOUT issuing a revision.
 *
 * The other end of `startCorrection`, and the one that mails nobody. Because no revision is
 * issued, the envelope never leaves revision N — so every link already in a recipient's inbox is
 * still a link for the revision it was minted against, nothing is revoked, and nothing has to be
 * sent to replace what did not die. That is the whole mechanism: quiet is not a suppressed
 * notification, it is a notification that was never necessary.
 *
 * Refused with PT409 by any of the five guards (ADR 0163) — somebody has signed, a document's
 * bytes moved, the pause was opened by a recipient's change request, a request is still open, or
 * the envelope is not paused at all. Each refusal carries its own remedy; `quietEditPreview` asks
 * the same question first so the screen only offers the road when it is open.
 */
export async function applyQuietEdit(input: {
  readonly idempotencyKey: string
  readonly envelopeId: string
  readonly note?: string | null
}): Promise<{
  readonly revisionNumber: number
  readonly fieldsAdded: number
  readonly fieldsRemoved: number
  readonly answersDiscarded: number
}> {
  const { data, error } = await client().rpc('apply_quiet_edit', {
    p_idempotency_key: input.idempotencyKey,
    p_envelope_id: input.envelopeId.replace(/^env_/, ''),
    p_note: input.note ?? null,
  })
  if (error) throw new WriteError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new WriteError('unexpected_shape', 'the edit could not be read back')
  }
  const row = data as Record<string, unknown>
  if (typeof row['revision_number'] !== 'number') {
    throw new WriteError('unexpected_shape', 'the edit came back without a revision')
  }
  const n = (key: string): number => (typeof row[key] === 'number' ? (row[key] as number) : 0)
  return {
    revisionNumber: row['revision_number'],
    fieldsAdded: n('fields_added'),
    fieldsRemoved: n('fields_removed'),
    answersDiscarded: n('answers_discarded'),
  }
}

/**
 * `public.issue_revision` — the corrected revision goes out.
 *
 * Every signature already given is carried forward or invalidated BY ITS HASH; the people with
 * something left to sign are re-invited with new links; the ones whose signatures all carried
 * forward are told nothing is needed. The answer is counts — never a participant.
 */
export async function issueRevision(input: {
  readonly idempotencyKey: string
  readonly envelopeId: string
  readonly note?: string | null
}): Promise<{
  readonly revisionNumber: number
  readonly carriedForward: number
  readonly invalidated: number
  readonly reInvited: number
  readonly notified: number
}> {
  // `POST /v1/envelopes/{id}/issue-revision` (ADR 0106) rather than the registered RPC, because the
  // Worker freezes the classified summary onto the revision in the SAME request — a browser that
  // issued through the RPC left the revision with no record of what changed until something else
  // computed one. The RPC stays registered in `infra/client-reads.json`: `check-advisors.mjs`
  // matches the advisor's WARN on `issue_revision` against that registry by name, so removing the
  // entry while the definer still grants EXECUTE to `authenticated` would turn a known, accepted
  // finding into an unaccepted one.
  const res = await api<{ readonly data?: Record<string, unknown> }>(
    `/v1/envelopes/${encodeURIComponent(input.envelopeId)}/issue-revision`,
    {
      method: 'POST',
      idempotencyKey: input.idempotencyKey,
      body: input.note == null || input.note === '' ? {} : { note: input.note },
    },
  )
  const row = res.data ?? {}
  const n = (key: string): number => (typeof row[key] === 'number' ? (row[key] as number) : 0)
  if (typeof row['revision_number'] !== 'number') {
    throw new WriteError('unexpected_shape', 'the revision came back without a number')
  }
  return {
    revisionNumber: row['revision_number'],
    carriedForward: n('carried_forward'),
    invalidated: n('invalidated'),
    reInvited: n('re_invited'),
    notified: n('notified'),
  }
}

/**
 * Create or edit one contact — `public.upsert_contact`.
 *
 * One function for both, because the difference is whether an id is supplied and the validation,
 * the permission and the duplicate rule are identical either way. Two wrappers would be two places
 * for the email check to drift.
 */
export async function upsertContact(input: {
  readonly id: string | null
  readonly name: string
  readonly email: string
  readonly company?: string | null
  readonly jobTitle?: string | null
  readonly defaultLocale?: string | null
  readonly notes?: string | null
}): Promise<Record<string, unknown>> {
  const { data, error } = await client().rpc('upsert_contact', {
    p_id: input.id,
    p_name: input.name,
    p_email: input.email,
    p_company: input.company ?? null,
    p_job_title: input.jobTitle ?? null,
    p_default_locale: input.defaultLocale ?? null,
    p_notes: input.notes ?? null,
  })
  if (error) throw new WriteError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new WriteError('unexpected_shape', 'the contact could not be read back')
  }
  return data as Record<string, unknown>
}

/** Soft-delete one contact — `public.delete_contact`. The address becomes reusable afterwards. */
export async function deleteContact(id: string): Promise<void> {
  const { error } = await client().rpc('delete_contact', { p_id: id })
  if (error) throw new WriteError(error.code ?? 'postgrest', error.message)
}
